import React from "react";
import { Card } from "@/components/ui/card";
import {
  Calendar, Truck, Storefront, EnvelopeSimple, Tag, NoteBlank, Cube, CheckCircle,
} from "@phosphor-icons/react";
import {
  deliveryBadge, formatDeliveryDate, formatFulfillmentType, BADGE_STYLES, hasOrderMeta,
} from "@/lib/orderFields";

function hasAnyMeta(order) {
  return hasOrderMeta(order) || !!order?.has_specialty_items || !!order?.specialty_payment_status;
}

/**
 * Full info card shown on Order Detail above the line items.
 * Renders only the rows whose underlying field exists.
 */
export default function OrderMetaCard({ order }) {
  if (!hasAnyMeta(order)) return null;

  const badge = deliveryBadge(order.delivery_date);
  const dateText = formatDeliveryDate(order.delivery_date);
  const fulfill = formatFulfillmentType(order.fulfillment_type);

  return (
    <Card className="p-5 border-zinc-200 dark:border-zinc-700" data-testid="order-meta-card">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(badge || dateText) && (
          <MetaRow icon={Calendar} label="Delivery / pickup date" testId="meta-row-date">
            <div className="flex items-center gap-2 flex-wrap">
              {badge && (
                <span
                  className="inline-flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-[0.15em]"
                  style={{
                    background: BADGE_STYLES[badge.tone].bg,
                    color: BADGE_STYLES[badge.tone].text,
                    borderColor: BADGE_STYLES[badge.tone].border,
                  }}
                >
                  {badge.label}
                </span>
              )}
              {dateText && <span className="font-semibold text-base">{dateText}</span>}
            </div>
          </MetaRow>
        )}

        {fulfill && (
          <MetaRow
            icon={fulfill === "Pickup" ? Storefront : Truck}
            label="Fulfillment type"
            testId="meta-row-fulfillment"
          >
            <span className={`inline-flex items-center gap-1.5 rounded border px-2.5 py-1 text-xs font-semibold ${
              fulfill === "Pickup"
                ? "bg-emerald-50 text-emerald-800 border-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300 dark:border-emerald-800"
                : "bg-blue-50 text-blue-800 border-blue-200 dark:bg-blue-950/40 dark:text-blue-300 dark:border-blue-800"
            }`}>
              {fulfill}
            </span>
          </MetaRow>
        )}

        {order.store && (
          <MetaRow icon={Storefront} label="Store" testId="meta-row-store">
            <span className="font-semibold">{order.store}</span>
          </MetaRow>
        )}

        {order.customer_email && (
          <MetaRow icon={EnvelopeSimple} label="Customer email" testId="meta-row-email">
            <a
              href={`mailto:${order.customer_email}`}
              className="font-mono text-sm text-[#C8102E] hover:underline"
            >
              {order.customer_email}
            </a>
          </MetaRow>
        )}

        {order.po_number && (
          <MetaRow icon={Tag} label="PO / Reference" testId="meta-row-po">
            <span className="font-mono font-semibold">{order.po_number}</span>
          </MetaRow>
        )}

        {order.has_specialty_items && (
          <MetaRow icon={Cube} label="Specialty items" testId="meta-row-specialty">
            <SpecialtySummary order={order} />
          </MetaRow>
        )}

        {order.specialty_payment_status && (
          <MetaRow icon={Tag} label="Specialty payment" testId="meta-row-specialty-payment">
            <span className="font-semibold">{prettyStatus(order.specialty_payment_status)}</span>
          </MetaRow>
        )}
      </div>

      {order.notes && (
        <div className="mt-4 pt-4 border-t border-zinc-200 dark:border-zinc-700" data-testid="meta-row-notes">
          <div className="flex items-center gap-2 mb-1.5">
            <NoteBlank size={14} weight="bold" className="text-zinc-500" />
            <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
              Special instructions
            </div>
          </div>
          <div className="text-sm text-zinc-700 dark:text-zinc-200 leading-relaxed bg-amber-50 dark:bg-amber-950/30 border-l-4 border-amber-400 px-3 py-2 rounded">
            {order.notes}
          </div>
        </div>
      )}
    </Card>
  );
}

function MetaRow({ icon: Icon, label, children, testId }) {
  return (
    <div data-testid={testId}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon size={14} weight="bold" className="text-zinc-500" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">{label}</div>
      </div>
      <div className="pl-5">{children}</div>
    </div>
  );
}

function SpecialtySummary({ order }) {
  const total = order.specialty_count || 0;
  const received = order.specialty_received_count || 0;
  const pct = total > 0 ? Math.round((received / total) * 100) : 0;
  const allDone = total > 0 && received >= total;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 flex-wrap">
        {allDone
          ? <CheckCircle size={16} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
          : <Cube size={16} weight="bold" className="text-purple-600 dark:text-purple-400" />}
        <span className="font-semibold">{received} of {total} received</span>
        {allDone
          ? <span className="text-xs font-bold uppercase tracking-widest text-emerald-700 dark:text-emerald-400">All in</span>
          : <span className="text-xs font-bold uppercase tracking-widest text-purple-700 dark:text-purple-400">Pending vendor</span>}
      </div>
      {total > 0 && (
        <div className="h-1.5 w-44 rounded-full bg-zinc-100 dark:bg-zinc-800 overflow-hidden">
          <div
            className={`h-full ${allDone ? "bg-emerald-500" : "bg-purple-500"}`}
            style={{ width: `${pct}%` }}
          />
        </div>
      )}
    </div>
  );
}

function prettyStatus(s) {
  if (typeof s !== "string") return String(s);
  return s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}
